import { FlaskConical, Wrench } from "lucide-react";
import { useTranslation } from "@ncp/i18n";
import { Badge, Field, NativeSelect, StatCard, Textarea } from "@ncp/ui";
import { CatalogPage } from "../features/generic/CatalogPage";
import { TOOL_KINDS, newToolBody, type ToolBody, type ToolSummary } from "../features/tools/api";

/** Agent tools: formulas, lookups and connector calls an administrator defines once and grants to any agent. */
export function ToolsPage() {
  const { t } = useTranslation();
  return (
    <CatalogPage<ToolSummary, ToolBody>
      resource="tools"
      basePath="/tools"
      icon={Wrench}
      title={t("admin:tools.title")}
      subtitle={t("admin:tools.subtitle")}
      navLabel={t("admin:nav.tools")}
      writePermission="platform.tools.write"
      newLabel={t("admin:tools.new")}
      emptyTitle={t("admin:tools.emptyTitle")}
      emptyBody={t("admin:tools.emptyBody")}
      docsAnchor="agent-tools"
      help={<><p>{t("admin:tools.help.what")}</p><p>{t("admin:tools.help.kinds")}</p><p>{t("admin:tools.help.tests")}</p></>}
      newBody={(name, nameAr, extra) => newToolBody(name, nameAr, extra)}
      createFields={(extra, set) => (
        <>
          <Field label={t("admin:tools.fields.kind")}>
            <NativeSelect data-testid="create-kind" value={extra.kind ?? "formula"} onChange={(e) => set("kind", e.target.value)}>
              {TOOL_KINDS.map((k) => <option key={k} value={k}>{t(`admin:tools.kinds.${k}`)}</option>)}
            </NativeSelect>
          </Field>
          <Field label={t("admin:tools.fields.purpose")} hint={t("admin:tools.fields.purposeHint")} required>
            <Textarea data-testid="create-purpose" rows={2} value={extra.purpose ?? ""} onChange={(e) => set("purpose", e.target.value)} />
          </Field>
        </>
      )}
      stats={(rows) => {
        const tested = rows.filter((r) => r.lastTestTotal);
        return <StatCard label={t("admin:tools.stats.tested")} value={tested.filter((r) => r.lastTestPassed === r.lastTestTotal).length} icon={FlaskConical} tone="neutral" footer={t("admin:tools.stats.testedHint")} />;
      }}
      columns={[
        { header: t("admin:tools.columns.kind"), cell: (r) => (r.kind ? <Badge variant="outline" size="sm">{t(`admin:tools.kinds.${r.kind}`)}</Badge> : null) },
        { header: t("admin:tools.columns.grant"), cell: (r) => <code className="font-mono text-xs">{r.toolKey ?? `tool.${r.key}`}</code> },
        { header: t("admin:tools.columns.tests"), cell: (r) => r.lastTestTotal ? <Badge variant={r.lastTestPassed === r.lastTestTotal ? "success" : "danger"} size="sm">{r.lastTestPassed}/{r.lastTestTotal}</Badge> : <span className="text-muted-foreground">—</span> },
      ]}
    />
  );
}
